import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL } from "@/data/content";
import { useSEO } from "@/hooks/useSite";

export default function AdminLogin() {
  const { login, isAuthed } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  useSEO({ title: "Admin Portal | IAUE School of Basic Studies" });

  useEffect(() => { if (isAuthed) navigate("/admin/dashboard", { replace: true }); }, [isAuthed, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-5" style={{ background: "radial-gradient(120% 100% at 50% 0%, #16347A, #0A1E4E 55%, #071538)" }}>
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-sm bg-[#F7F5EF] p-8 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="IAUE crest" className="h-16 w-16 object-contain" />
          <h1 className="mt-4 font-serif text-2xl font-bold text-[#0A1E4E]">Admin Portal</h1>
          <p className="text-sm text-slate-500">School of Basic Studies — Content Management</p>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1.5 w-full rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 outline-none focus:border-[#0A1E4E] focus:ring-2 focus:ring-[#0A1E4E]/15" data-testid="admin-email-input" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="mt-1.5 w-full rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 outline-none focus:border-[#0A1E4E] focus:ring-2 focus:ring-[#0A1E4E]/15" data-testid="admin-password-input" />
          </div>
          <button type="submit" className="btn-navy w-full !py-3.5" disabled={loading} data-testid="admin-login-button">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Signing in…</> : <><ShieldCheck size={16} /> Sign In</>}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
